import React, { Component, type KeyboardEvent } from 'react';
import cx from 'classnames';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ArrowDownIcon from '@atlaskit/icon/glyph/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/glyph/arrow-up';
import { injectIntl } from '@atlassian/jira-intl';
import { Tooltip } from '@atlassian/jira-portfolio-3-common';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { getFieldKey } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/raw-issues/sort-utils';
import { defaultSorting } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/visualisations';
import type { Sorting } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/visualisations/types';
import { getSortConfiguration } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/view-settings/sort-by/utils';
import { SORT_DIRECTION } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { KEYS } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/interaction-constants';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { fireUIAnalytics, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import traceUFOPress from '@atlassian/ufo-trace-press';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

// eslint-disable-next-line jira/react/no-class-components
class SortButton extends Component<Props> {
	// @ts-expect-error Property 'analyticsEvent' has no initializer and is not definitely assigned in the constructor
	analyticsEvent: UIAnalyticsEvent;

	getSortBy = (): Sorting => {
		const {
			column: { attribute },
			sorting,
			customFields,
		} = this.props;

		const { field, direction } = sorting;
		let nextDirection =
			attribute === 'priority' ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING;

		if (attribute === field) {
			if (field === 'priority' && direction === SORT_DIRECTION.ASCENDING) {
				return defaultSorting;
			}
			if (field === 'priority' && direction === SORT_DIRECTION.DESCENDING) {
				return getSortConfiguration(attribute, customFields, SORT_DIRECTION.ASCENDING);
			}
			if (direction === SORT_DIRECTION.DESCENDING) {
				return defaultSorting;
			}
			if (direction === SORT_DIRECTION.ASCENDING) {
				nextDirection = SORT_DIRECTION.DESCENDING;
			}
		}

		return getSortConfiguration(attribute, customFields, nextDirection);
	};

	fireAnalytics = (): void => {
		if (!this.analyticsEvent) return;
		const { planningUnit } = this.props;
		const sortConfiguration = this.getSortBy();

		const [actionSubject, action, actionSubjectId] =
			PRODUCT_ANALYTICS_EVENT_NAMES.SORT_BY_CLICKED.split(' ');
		fireUIAnalytics(this.analyticsEvent.update({ actionSubject, action }), actionSubjectId, {
			sortByField:
				sortConfiguration.isCustomField === true
					? `customfield_${sortConfiguration.type}`
					: getFieldKey(sortConfiguration.field, planningUnit),
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onClick = (e: any) => {
		const { isDisabled = false, changeSort } = this.props;
		e.stopPropagation();

		if (isDisabled) return;

		traceUFOPress(KEYS.SORT_BY_FIELD);
		this.fireAnalytics();
		changeSort(this.getSortBy());
	};

	onKeyDown = (event: KeyboardEvent<EventTarget>) => {
		const { isDisabled = false, changeSort } = this.props;
		event.stopPropagation();

		if (isDisabled) return;
		if (event.key === 'Enter' || event.key === ' ') {
			traceUFOPress(KEYS.SORT_BY_FIELD);
			this.fireAnalytics();
			changeSort(this.getSortBy());
		}
	};

	getArrows = () => {
		const {
			sorting,
			column: { attribute },
			intl,
			isDisabled = false,
		} = this.props;
		if (attribute === 'priority') {
			return (
				<Tooltip
					content={
						isDisabled ? intl.formatMessage(commonMessages.disabledSortByPriorityTooltip) : null
					}
				>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<span className={isDisabled ? styles.paddingOld : ''}>
						{sorting.field === attribute && sorting.direction === SORT_DIRECTION.ASCENDING ? (
							<ArrowUpIcon
								testId="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.fields.header.sort-button.arrow-up-icon-ascending"
								label=""
								size="small"
								primaryColor="currentColor"
							/>
						) : (
							<ArrowDownIcon
								testId="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.fields.header.sort-button.arrow-down-icon-descending"
								label=""
								size="small"
								primaryColor="currentColor"
							/>
						)}
					</span>
				</Tooltip>
			);
		}
		return sorting.field === attribute && sorting.direction === SORT_DIRECTION.DESCENDING ? (
			<ArrowDownIcon
				testId="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.fields.header.sort-button.arrow-down-icon-descending"
				label=""
				size="small"
				primaryColor="currentColor"
			/>
		) : (
			<ArrowUpIcon
				testId="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.fields.header.sort-button.arrow-up-icon-ascending"
				label=""
				size="small"
				primaryColor="currentColor"
			/>
		);
	};

	render() {
		const {
			intl,
			column: { attribute, isSortable },
			sorting,
			customClass,
			isDisabled,
		} = this.props;

		if (isSortable !== true) {
			return null;
		}

		return (
			<>
				<MountEvent
					onMount={(analyticsEvent) => {
						this.analyticsEvent = analyticsEvent;
					}}
				/>
				<span
					aria-label={intl.formatMessage(messages.sortButton)}
					tabIndex={0}
					role="button"
					onKeyDown={(e) => this.onKeyDown(e)}
					onClick={(e) => this.onClick(e)}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={cx(customClass, styles.sortTrigger, {
						[styles.active]: sorting.field === attribute,
						[styles.isDisabled]: isDisabled,
					})}
				>
					{this.getArrows()}
				</span>
			</>
		);
	}
}

export default injectIntl(SortButton);
